import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { BillerSearch } from "@/components/BillerSearch";
import { api, formatApiError } from "@/lib/api";
import { BBPS_CATEGORIES, formatINR } from "@/lib/constants";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, XCircle, Loader2, Lock, User, Wallet } from "lucide-react";
import { useFeatures } from "@/hooks/useFeatures";

export default function BbpsFlow() {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [stage, setStage] = useState("category"); // category | fetch | confirm | result
  const [category, setCategory] = useState(null);
  const [billers, setBillers] = useState([]);
  const [billerId, setBillerId] = useState("");
  const [consumerId, setConsumerId] = useState("");
  const [consumerName, setConsumerName] = useState("");
  const [bill, setBill] = useState(null);
  const [fees, setFees] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Phase 7.18 — self transaction + customer wallet
  const [isSelf, setIsSelf] = useState(true);  // Phase 7.37h — default to self-mode
  const [customerMobile, setCustomerMobile] = useState("");
  const [customerInfo, setCustomerInfo] = useState(null);  // {balance, kyc_status, customer_name}
  const [partnerMarginOverride, setPartnerMarginOverride] = useState("");

  const lookupCustomer = async () => {
    if (!/^[6-9]\d{9}$/.test(customerMobile)) { toast.error("Enter a valid 10-digit mobile"); return; }
    try {
      const { data } = await api.get(`/customer-wallet/balance/${customerMobile}`);
      setCustomerInfo(data);
      if (data.kyc_status !== "verified") {
        toast.warning(`Customer KYC: ${data.kyc_status}. Verify before transacting.`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lookup failed");
    }
  };

  const pickCategory = async (cat) => {
    // Phase 7.20 — ALL BBPS categories now use the same customer-pays + OTP wizard
    // (previously only credit-card / loan-emi). Self-mode toggle on the v2 wizard
    // delegates back to the legacy partner-wallet flow when needed.
    if (isSelf) {
      // Self-mode: keep using the legacy v1 quick-pay flow (partner wallet, no
      // customer OTP). The v2 wizard's self-mode applies only to credit-card / loan-emi.
      if (cat.id === "credit-card" || cat.id === "loan-emi") {
        navigate(`/bbps/v2?category=${cat.id === "credit-card" ? "credit_card" : "loan_emi"}&self=1`);
        return;
      }
      // For other categories in self-mode, stay on this page's quick-fetch + pay flow.
    } else {
      const snake = cat.id.replace(/-/g, "_");
      navigate(`/bbps/v2?category=${snake}`);
      return;
    }
    setCategory(cat);
    setStage("fetch");
    try {
      const { data } = await api.get(`/bbps/billers/${cat.id}`);
      setBillers(data);
      setBillerId(data[0]?.id || "");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load billers");
    }
  };

  const fetchBill = async (e) => {
    e.preventDefault();
    if (!consumerId.trim()) { toast.error("Enter consumer number"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/bbps/bill-fetch", {
        category: category.id,
        consumer_id: consumerId.trim(),
        biller_id: billerId,
      });
      setBill(data.bill);
      setFees(data.fees);
      setConsumerName(data.bill.consumer_name);
      setStage("confirm");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Bill fetch failed");
    } finally {
      setLoading(false);
    }
  };

  const payBill = async () => {
    if (!isSelf && !/^[6-9]\d{9}$/.test(customerMobile)) {
      toast.error("Customer mobile is required (or toggle 'Self transaction')");
      return;
    }
    setLoading(true);
    try {
      const body = {
        category: category.id,
        consumer_id: bill.consumer_id,
        consumer_name: consumerName,
        biller_id: bill.biller_id,
        biller_name: bill.biller_name,
        amount: bill.bill_amount,
      };
      if (isSelf) {
        body.is_self = true;
      } else {
        body.customer_mobile = customerMobile;
        if (partnerMarginOverride !== "") body.partner_margin_override = parseFloat(partnerMarginOverride);
      }
      const { data } = await api.post("/bbps/pay", body);
      const txn = data.transaction || data;
      setResult(txn);
      setStage("result");
      refreshUser();
      if (txn.status === "success") toast.success("Payment successful");
      else if (String(txn.status).startsWith("pending_l")) toast.success("Submitted · awaiting L1 approval");
      else toast.error(txn.message || "Payment failed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Payment failed");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStage("category");
    setCategory(null);
    setBill(null);
    setFees(null);
    setResult(null);
    setConsumerId("");
    setConsumerName("");
    setIsSelf(false);
    setCustomerMobile("");
    setCustomerInfo(null);
    setPartnerMarginOverride("");
  };

  return (
    <DashboardLayout>
      {stage === "category" && (
        <>
          <div className="mb-6 surface-card p-4 max-w-2xl" data-testid="bbps-mode-banner">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Pay for</span>
              <div className="inline-flex rounded-md border bg-background p-1" role="tablist">
                <button
                  type="button"
                  onClick={() => setIsSelf(false)}
                  data-testid="bbps-mode-customer"
                  className={`px-4 py-1.5 text-sm font-medium rounded transition-colors ${!isSelf ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}
                >
                  Customer
                </button>
                <button
                  type="button"
                  onClick={() => { setIsSelf(true); setCustomerMobile(""); setCustomerInfo(null); }}
                  data-testid="bbps-mode-self"
                  className={`px-4 py-1.5 text-sm font-medium rounded transition-colors ${isSelf ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}
                >
                  <User className="h-3.5 w-3.5 inline mr-1" /> Self transaction
                </button>
              </div>
              <span className="text-xs text-muted-foreground">
                {isSelf ? "Pay from your own wallet — no customer KYC needed" : "Debit customer's wallet · partner earns commission"}
              </span>
            </div>
          </div>
          <CategoryGrid onPick={pickCategory} />
        </>
      )}

      {stage !== "category" && (
        <div className="max-w-2xl">
          <button
            type="button"
            onClick={() => (stage === "fetch" ? reset() : setStage("fetch"))}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            data-testid="bbps-back"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            {stage === "fetch" ? "All categories" : "Edit consumer"}
          </button>
          <div className="mt-3 flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-primary text-primary-foreground grid place-items-center">
              <category.icon className="h-5 w-5" strokeWidth={1.5} />
            </div>
            <div>
              <span className="overline">BBPS</span>
              <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
                {category.label} bill payment
              </h1>
            </div>
          </div>
        </div>
      )}

      {stage === "fetch" && category && (
        <form onSubmit={fetchBill} className="mt-8 max-w-2xl surface-card p-6 space-y-5" data-testid="bbps-fetch-form">
          <div>
            <Label>Biller</Label>
            <BillerSearch
              billers={billers}
              loading={false}
              value={billerId}
              onSelect={setBillerId}
            />
          </div>
          <div>
            <Label>{category.placeholder || "Consumer number"}</Label>
            <Input
              data-testid="bbps-consumer-id"
              value={consumerId}
              onChange={(e) => setConsumerId(e.target.value)}
              className="mt-1 font-mono"
              placeholder={category.placeholder}
            />
          </div>

          {/* Self transaction toggle + Customer mobile (Phase 7.18) */}
          <div className="rounded-md border bg-accent/30 p-3 space-y-3">
            <div className="text-xs flex items-center gap-2">
              <span className="font-medium">Mode:</span>
              <Badge variant={isSelf ? "default" : "outline"} className={isSelf ? "" : "bg-emerald-50 text-emerald-700 border-emerald-200"} data-testid="bbps-mode-badge">
                {isSelf ? <><User className="h-3 w-3 mr-1" />Self transaction</> : <><User className="h-3 w-3 mr-1" />Customer payment</>}
              </Badge>
              <button type="button" className="text-primary underline ml-auto" onClick={reset} data-testid="bbps-change-mode">Change mode</button>
            </div>
            {!isSelf && (
              <div className="space-y-2" data-testid="bbps-customer-block">
                <Label>Customer mobile · 10 digits</Label>
                <div className="flex gap-2">
                  <Input
                    data-testid="bbps-customer-mobile"
                    value={customerMobile}
                    onChange={(e) => { setCustomerMobile(e.target.value.replace(/\D/g, "").slice(0, 10)); setCustomerInfo(null); }}
                    placeholder="9999999999"
                    className="font-mono"
                  />
                  <Button type="button" variant="outline" onClick={lookupCustomer} disabled={!/^[6-9]\d{9}$/.test(customerMobile)} data-testid="bbps-customer-lookup">
                    Look up
                  </Button>
                </div>
                {customerInfo && (
                  <div className="text-xs flex items-center gap-3 flex-wrap" data-testid="bbps-customer-info">
                    <Badge variant={customerInfo.kyc_status === "verified" ? "default" : "outline"} className={customerInfo.kyc_status === "verified" ? "bg-emerald-100 text-emerald-700" : "border-amber-300 text-amber-800"}>
                      KYC: {customerInfo.kyc_status}
                    </Badge>
                    <span className="inline-flex items-center gap-1"><Wallet className="h-3 w-3" /> Balance: <strong className="font-mono">{formatINR(customerInfo.balance)}</strong></span>
                    {customerInfo.kyc_status !== "verified" && (
                      <a href={`/customer-kyc?mobile=${customerMobile}&return=/bbps`} className="text-primary underline">Verify KYC →</a>
                    )}
                    <a href={`/customer-wallet?mobile=${customerMobile}`} className="text-primary underline">Top-up →</a>
                  </div>
                )}
                <div>
                  <Label className="text-xs">Partner margin override (₹) · optional</Label>
                  <Input
                    type="number"
                    data-testid="bbps-margin-override"
                    value={partnerMarginOverride}
                    onChange={(e) => setPartnerMarginOverride(e.target.value)}
                    placeholder="Leave blank to use package default"
                    className="font-mono mt-1"
                  />
                </div>
              </div>
            )}
          </div>

          <Button type="submit" disabled={loading} data-testid="bbps-fetch-submit">
            {loading ? "Fetching…" : "Fetch bill"}
          </Button>
        </form>
      )}

      {stage === "confirm" && bill && fees && (
        <div className="mt-8 max-w-2xl surface-card p-6 space-y-5 animate-fade-up" data-testid="bbps-confirm">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Biller" value={bill.biller_name} />
            <Field label="Bill number" value={bill.bill_number} mono />
            <Field label="Consumer" value={bill.consumer_name} />
            <Field label="Due date" value={bill.due_date} mono />
          </div>
          <div className="pt-4 border-t border-border">
            <div className="overline">Fee breakdown</div>
            <dl className="mt-3 space-y-2 text-sm">
              <Row label="Bill amount" value={formatINR(bill.bill_amount)} />
              <Row label="Platform fee" value={formatINR(fees.platform_fee)} muted />
              <Row label="Wallet debit (you pay)" value={formatINR(fees.wallet_debit)} strong />
              <Row label="Partner margin (from consumer)" value={formatINR(fees.partner_margin)} muted />
              <Row label="Net earnings" value={formatINR(fees.net_earnings)} success />
              <Row label="Consumer total" value={formatINR(fees.consumer_total)} strong />
            </dl>
          </div>
          <Button onClick={payBill} disabled={loading} className="w-full" size="lg" data-testid="bbps-pay-submit">
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing…</> :
              isSelf
                ? `Pay ${formatINR(fees.wallet_debit)} from MY wallet`
                : `Pay ${formatINR(fees.consumer_total)} from CUSTOMER wallet`
            }
          </Button>
        </div>
      )}

      {stage === "result" && result && (
        <div className="mt-8 max-w-2xl surface-card p-8 text-center animate-fade-up" data-testid="bbps-result">
          {result.status === "success" ? (
            <CheckCircle2 className="h-12 w-12 mx-auto text-success" strokeWidth={1.5} />
          ) : String(result.status).startsWith("pending_l") ? (
            <Loader2 className="h-12 w-12 mx-auto text-warning" strokeWidth={1.5} />
          ) : (
            <XCircle className="h-12 w-12 mx-auto text-destructive" strokeWidth={1.5} />
          )}
          <h2 className="font-display text-2xl font-bold mt-4">
            {result.status === "success" ? "Payment successful"
              : String(result.status).startsWith("pending_l") ? "Submitted for approval"
              : "Payment failed"}
          </h2>
          <p className="text-sm text-muted-foreground mt-2">
            {String(result.status).startsWith("pending_l")
              ? "Credit-card bill payments require L1 → L2 → L3 review. Wallet has been debited; you'll be auto-refunded if rejected."
              : (result.message || "")}
          </p>
          <div className="mt-6 grid sm:grid-cols-2 gap-4 text-left">
            <Field label="Amount" value={formatINR(result.amount)} mono />
            <Field label="Platform fee" value={formatINR(result.platform_fee)} mono />
            <Field label="Provider ref" value={result.provider_ref} mono />
            <Field label="Net earnings" value={formatINR(result.net_earnings)} mono />
          </div>
          <div className="mt-6 flex gap-3 justify-center">
            <Button variant="outline" onClick={reset} data-testid="bbps-new-txn">New transaction</Button>
            <Button onClick={() => navigate("/transactions")} data-testid="bbps-view-history">View history</Button>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function CategoryGrid({ onPick }) {
  const { isEnabled, limit } = useFeatures();
  const visible = BBPS_CATEGORIES.filter((c) => !c.featureKey || isEnabled(c.featureKey));
  const fmtCap = (n) => {
    if (!n) return null;
    if (n >= 100000) return `₹${(n / 100000).toFixed(n % 100000 === 0 ? 0 : 1)}L`;
    return `₹${(n / 1000).toFixed(0)}k`;
  };
  return (
    <div>
      <span className="overline">BBPS</span>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
        Pay a bill for your consumer
      </h1>
      <p className="text-muted-foreground mt-2 max-w-xl">
        Choose a category. Credit card bill payments are the primary lever and are highlighted.
      </p>
      {visible.length === 0 ? (
        <div className="mt-8 surface-card p-12 text-center" data-testid="bbps-no-categories">
          <Lock className="h-8 w-8 mx-auto text-muted-foreground" strokeWidth={1.5} />
          <p className="mt-3 text-sm text-muted-foreground">All BBPS categories are disabled for your account. Ask your admin to enable at least one in <span className="font-mono text-xs">Feature matrix</span>.</p>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="bbps-category-grid">
          {visible.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onPick(c)}
              data-testid={`bbps-cat-${c.id}`}
              className={[
                "group text-left surface-card p-5 interactive-card",
                c.highlight ? "ring-2 ring-secondary" : "",
              ].join(" ")}
            >
              <div
                className={[
                  "h-10 w-10 rounded-md grid place-items-center transition-all",
                  c.highlight ? "bg-secondary text-secondary-foreground" : "bg-accent text-primary group-hover:bg-primary group-hover:text-primary-foreground",
                ].join(" ")}
              >
                <c.icon className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <div className="mt-4 font-semibold">{c.label}</div>
              {c.featureKey && fmtCap(limit(c.featureKey)) && (
                <div className="text-[11px] font-mono text-muted-foreground mt-1" data-testid={`bbps-cap-${c.id}`}>
                  Up to {fmtCap(limit(c.featureKey))}
                </div>
              )}
              {c.highlight && <Badge className="mt-2 bg-secondary text-secondary-foreground">Primary</Badge>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="overline">{label}</div>
      <div className={`mt-1 text-sm ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function Row({ label, value, muted, strong, success }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${muted ? "text-muted-foreground" : ""}`}>{label}</span>
      <span className={[
        "font-mono tabular-nums",
        strong ? "font-semibold" : "",
        success ? "text-success font-semibold" : "",
      ].join(" ")}>{value}</span>
    </div>
  );
}
